import { useCallback, useEffect, useRef } from "react";

/**
 * Roving D-pad focus for the home page (TV-first roadmap): ArrowLeft/Right
 * move within a row (hero actions, a content rail) and ArrowUp/Down move
 * between rows, skipping rows with nothing focusable (e.g. the empty
 * Continue Watching state). The position is STATE (not derived from the DOM)
 * so mouse clicks and keyboard agree, and the focused element is located by
 * data attributes afterwards — the same pattern as the EPG guide's
 * useGuideFocus.
 *
 * Focus is never stolen on mount; the scroll/focus pass only runs after the
 * user has navigated with arrows or clicked a tile.
 *
 * The position is held in a ref and the ring is moved on the elements
 * themselves, NOT through React state: re-rendering the whole page on every
 * arrow press cost ~490 ms per press on a phone (and over two seconds on the
 * 1 GB projector), while moving the ring by hand costs ~30 ms (2026-09-20).
 * Renders that happen for other reasons read the same ref, so the ring survives
 * them.
 */
export interface HomeRow {
  id: string;
  /** Number of focusable items in this row (0 = skip when moving vertically). */
  cols: number;
}

export interface HomeFocus {
  /** True when (row, col) is the current target — drives roving tabIndex + ring
   * as a row renders. Stable: it reads the position ref, so it never re-renders
   * anything by itself. */
  isFocused: (row: number, col: number) => boolean;
  /** The focused column of one row, or -1 — for rows rendered as memoised components. */
  focusedCol: (row: number) => number;
  /** Move focus programmatically (syncs a mouse click on a tile/button). */
  focusAt: (row: number, col: number) => void;
}

/**
 * Back to the very top, so the hero banner is whole again. Reaching the top row
 * with `block: "nearest"` only brings the focused button into view, which left
 * the banner cropped for the rest of the session (owner, 2026-09-20).
 */
export function showBanner(): void {
  const page = document.querySelector<HTMLElement>(".app__content");
  page?.scrollTo({ top: 0, behavior: "smooth" });
}

/**
 * A row said it had a column, and the page has nothing there.
 *
 * This is the shape of the bug that made the hero's Trailer button
 * unreachable (owner, 2026-09-22): `rows` declared three columns whenever a
 * trailer existed, while only Play Now and Open Guide carried
 * `data-home-col`. Nothing checked the two against each other, so the cursor
 * moved into a gap — the press appeared to do nothing, and the next one
 * skipped the button entirely.
 *
 * Nothing is thrown: a rail whose data has not arrived is a legitimate empty
 * column for a moment, and the retry above covers it. After a second and a
 * half of looking, though, no element is ever going to appear, and the only
 * way anyone found the last one was the owner pressing Right on a TV. Said
 * once per coordinate so a stuck cursor cannot flood the log.
 */
const reportedGaps = new Set<string>();

function reportEmptyColumn(row: number, col: number): void {
  const at = `${row},${col}`;
  if (reportedGaps.has(at)) return;
  reportedGaps.add(at);
  console.warn(
    `[home] focus row ${row} declares column ${col}, but no element carries ` +
      `data-home-row="${row}" data-home-col="${col}". The D-pad will appear to ` +
      `do nothing there. Check the row's cols against what it renders.`,
  );
}

/**
 * Is this control part of the FIXED chrome at or above Home — the top bar, or
 * the rail's own items down to and including Home?
 *
 * The owner's rule, 2026-09-22: "collapse is above home so it does the same as
 * home. live tv down stays where it is… that also means notifications brings it
 * to the top since above home". The cut is Home's own position, read off the
 * layout rather than a list of controls — the top bar sits above the rail, the
 * brand and the collapse arrow sit above Home, Live TV and everything after it
 * sit below. A list would drift as the rail changes; this cannot.
 *
 * FIXED assets only, and that distinction is load-bearing (owner, 2026-09-22:
 * "home screen or above only includes fixed assets correct not rows that can
 * technically scroll above?"). The content rows scroll, so a tile can sit above
 * Home's position while the page is scrolled down. It is not the top of the
 * screen, and reaching it must not pull the page back — otherwise the rule would
 * fire from the middle of the catalogue depending on scroll position, which is
 * the exact "too aggressive on navigation" behaviour it was written to avoid.
 * The hero row is the one content row that does count, and it is named
 * separately below because it IS the banner.
 *
 * The top bar is checked first so the rule still works on a layout with no rail
 * (a narrow phone), and a control with no box is never "above" anything.
 */
export function reachesTop(el: HTMLElement): boolean {
  if (el.closest(".topbar")) return true;
  if (!el.closest(".navrail")) return false;
  const home = document.querySelector<HTMLElement>('.navrail a[href="/"]');
  if (!home) return false;
  const box = el.getBoundingClientRect();
  if (box.height < 1) return false;
  // A couple of pixels of tolerance: the two are stacked, not aligned to the pixel.
  return box.top <= home.getBoundingClientRect().top + 2;
}

/**
 * The column in `row` whose element is centred nearest `from` across the
 * screen, or null when the row has nothing drawn yet.
 */
function columnBelow(from: HTMLElement | null, row: number): number | null {
  if (!from) return null;
  const box = from.getBoundingClientRect();
  if (box.width < 1) return null;
  const middle = (box.left + box.right) / 2;
  let best: number | null = null;
  let bestGap = Infinity;
  for (const el of document.querySelectorAll<HTMLElement>(`[data-home-row="${row}"][data-home-col]`)) {
    const r = el.getBoundingClientRect();
    if (r.width < 1) continue;
    const gap = Math.abs((r.left + r.right) / 2 - middle);
    if (gap < bestGap) {
      bestGap = gap;
      best = Number(el.dataset.homeCol);
    }
  }
  return best !== null && Number.isFinite(best) ? best : null;
}

/** The ring class a target wears: tiles carry their own, buttons share one. */
function ringClass(el: HTMLElement): string {
  return el.classList.contains("home-tile") ? "home-tile--focused" : "home-focused";
}

export function useHomeFocus(rows: HomeRow[]): HomeFocus {
  const movedRef = useRef(false);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const focusRef = useRef({ row: 0, col: 0 });
  /** The element wearing the ring right now. */
  const targetRef = useRef<HTMLElement | null>(null);
  const timerRef = useRef(0);

  /** Move the ring, the roving tabindex and the browser's focus to (row, col). */
  const apply = useCallback((scroll: boolean) => {
    window.clearInterval(timerRef.current);
    let attempts = 0;
    const settle = () => {
      const { row, col } = focusRef.current;
      const el = document.querySelector<HTMLElement>(`[data-home-row="${row}"][data-home-col="${col}"]`);
      if (!el) {
        if (++attempts > 30) {
          window.clearInterval(timerRef.current);
          reportEmptyColumn(row, col);
        }
        return;
      }
      window.clearInterval(timerRef.current);
      const previous = targetRef.current;
      if (previous && previous !== el) {
        previous.classList.remove("home-tile--focused", "home-focused");
        previous.tabIndex = -1;
      }
      el.classList.add(ringClass(el));
      el.tabIndex = 0;
      targetRef.current = el;
      // Never steal focus from an open dialog (program detail, keypad).
      const active = document.activeElement;
      if (active instanceof HTMLElement && active.closest('[role="dialog"]')) return;
      if (el !== active) {
        el.focus({ preventScroll: !scroll });
        if (scroll) el.scrollIntoView({ block: "nearest", inline: "nearest" });
      }
      // The top row is the hero: show all of it, not just the focused button.
      if (scroll && row === 0) showBanner();
    };
    settle();
    if (!targetRef.current || targetRef.current.dataset.homeRow !== String(focusRef.current.row)) {
      timerRef.current = window.setInterval(settle, 50);
    }
  }, []);

  useEffect(() => () => window.clearInterval(timerRef.current), []);

  const clampCol = useCallback((row: number, col: number): number => {
    const count = rowsRef.current[row]?.cols ?? 0;
    if (count === 0) return 0;
    return Math.min(Math.max(col, 0), count - 1);
  }, []);

  const focusAt = useCallback(
    (row: number, col: number) => {
      const current = rowsRef.current;
      if (row < 0 || row >= current.length || current[row]!.cols === 0) return;
      const nextCol = clampCol(row, col);
      // Idempotent: re-clicking the already-focused target must not re-trigger
      // the focus pass (which could steal focus from an open dialog).
      if (movedRef.current && row === focusRef.current.row && nextCol === focusRef.current.col) return;
      movedRef.current = true;
      focusRef.current = { row, col: nextCol };
      apply(true);
    },
    [clampCol, apply],
  );

  /** Returns false when focus could not move (an edge), so the key can pass on. */
  const move = useCallback(
    (dRow: number, dCol: number): boolean => {
      const rows = rowsRef.current;
      const base = movedRef.current ? focusRef.current : { row: 0, col: 0 };
      let row = base.row + dRow;
      if (row < 0) row = 0;
      if (row >= rows.length) row = Math.max(0, rows.length - 1);
      // Skip rows with nothing focusable when moving vertically.
      if (dRow !== 0) {
        while (row >= 0 && row < rows.length && rows[row]!.cols === 0) row += dRow;
        if (row < 0) row = 0;
        if (row >= rows.length) row = Math.max(0, rows.length - 1);
      }
      // Up/Down: the tile visually above or below, not the same column number.
      // Rows scroll on their own, so the 5th tile of one row sat far from the
      // 5th of the next and Down jumped sideways (projector, 2026-09-27).
      const col = dRow !== 0 && movedRef.current && row !== base.row ? (columnBelow(targetRef.current, row) ?? clampCol(row, base.col)) : clampCol(row, base.col + dCol);
      // The first press only lands focus on the first tile; it always counts.
      if (movedRef.current && row === focusRef.current.row && col === focusRef.current.col) {
        // Up from the top row: the press passes on to the nav rail or top bar,
        // and the banner should be whole again behind it.
        if (dRow < 0) showBanner();
        return false;
      }
      movedRef.current = true;
      focusRef.current = { row, col };
      apply(true);
      return true;
    },
    [clampCol, apply],
  );

  // Arrows are handled at the window level (like the live page's quick-zap) so
  // a TV remote's D-pad works no matter where focus sits on the page. Guards:
  // never hijack arrows while typing in a field or while a dialog is open.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target;
      // Not while the player fills the screen: Home is behind it. Up on a film
      // started from Home moved Home's hidden ring instead of bringing up the
      // player's bar (full-screen audit, 2026-09-29) — the guide already
      // stands aside the same way.
      if (document.querySelector(".player-surface--fullscreen")) return;
      // A row's "See all" belongs to that row: Left and Down go into its tiles,
      // Right stays. Left to the app's navigation, it skipped the tile beside
      // it for the nav rail, which lay level with it (projector, 2026-09-29).
      const seeAll = target instanceof HTMLElement ? target.closest<HTMLElement>(".ui-section__action") : null;
      if (seeAll && !event.defaultPrevented) {
        const tile = seeAll.closest(".ui-section")?.querySelector<HTMLElement>("[data-home-row][data-home-col]");
        const row = tile ? Number(tile.dataset.homeRow) : NaN;
        if (!Number.isFinite(row)) return;
        if (event.key === "ArrowRight") {
          event.preventDefault();
        } else if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
          event.preventDefault();
          movedRef.current = true;
          focusRef.current = { row, col: columnBelow(seeAll, row) ?? 0 };
          apply(true);
        }
        return;
      }
      if (
        target instanceof HTMLElement &&
        (target.tagName === "INPUT" ||
          target.tagName === "SELECT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable ||
          target.closest('[role="dialog"]'))
      ) {
        return;
      }
      // Home's rows own the arrows only while focus is in them (or nowhere
      // yet). With focus in the nav rail, top bar or player, the app-wide D-pad
      // navigation moves it (components/spatialNav.ts).
      if (target instanceof HTMLElement && target !== document.body && !target.closest("[data-home-row]")) return;
      const moved =
        event.key === "ArrowUp" ? move(-1, 0)
        : event.key === "ArrowDown" ? move(1, 0)
        : event.key === "ArrowLeft" ? move(0, -1)
        : event.key === "ArrowRight" ? move(0, 1)
        : null;
      // An arrow that moved nothing (Left from the first column, Up from the
      // hero) passes on — to the nav rail, the top bar. Right at the end of a
      // row stays: a row is a lane. Passed on, Right from My List's one tile
      // went up to the banner's Open Guide, and Right from Open Guide down to
      // "See all" (projector, 2026-09-29).
      if (moved || (event.key === "ArrowRight" && target instanceof HTMLElement && target.closest("[data-home-row]"))) event.preventDefault();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [move, apply]);

  // Right from the nav rail enters Home at the row BESIDE the rail item, its
  // first tile on screen. Left to the app's navigation it found nothing lined
  // up with Home's rail item and went up to the search box (projector,
  // 2026-09-27) — the guide had the same fault. Capture phase, ahead of it.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "ArrowRight" || event.defaultPrevented) return;
      const from = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      if (!from?.closest(".navrail")) return;
      if (document.querySelector('[role="dialog"], [role="menu"], .player-surface--fullscreen')) return;
      const box = from.getBoundingClientRect();
      const middle = (box.top + box.bottom) / 2;
      let best: HTMLElement | null = null;
      let bestScore = Infinity;
      for (const el of document.querySelectorAll<HTMLElement>("[data-home-row][data-home-col]")) {
        const r = el.getBoundingClientRect();
        if (r.width < 1 || r.height < 1 || r.bottom <= 0 || r.top >= window.innerHeight || r.right <= box.right || r.left >= window.innerWidth) continue;
        // The nearest row vertically, then its leftmost tile on screen.
        const score = Math.abs((r.top + r.bottom) / 2 - middle) * 10 + (r.left - box.right) / 10;
        if (score < bestScore) {
          bestScore = score;
          best = el;
        }
      }
      if (!best) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      best.focus({ preventScroll: true });
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, []);

  // Focus can arrive on a tile from outside — the app-wide D-pad navigation
  // (Right from the nav rail), a tap. Adopt that tile as the position, so the
  // next arrow moves from where the user actually is.
  useEffect(() => {
    const onFocusIn = (event: FocusEvent) => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      // The banner comes back in full when focus is at or above Home — the top
      // bar, the rail's collapse arrow, Home itself — or when the viewer steps
      // onto the hero row, which is the banner. Below Home the page is left
      // exactly where it is: pulling a scrolled page to the top under a moving
      // cursor was too aggressive to navigate (owner, 2026-09-22).
      if (target && reachesTop(target)) showBanner();
      const el = target?.closest<HTMLElement>("[data-home-row]") ?? null;
      if (!el) return;
      const row = Number(el.dataset.homeRow);
      const col = Number(el.dataset.homeCol);
      if (!Number.isFinite(row) || !Number.isFinite(col)) return;
      // The hero row IS the banner: arriving on its buttons shows all of it.
      if (row === 0) showBanner();
      movedRef.current = true;
      if (focusRef.current.row === row && focusRef.current.col === col) return;
      focusRef.current = { row, col };
      // The element already has focus; only the ring and the roving tabindex move.
      apply(false);
    };
    document.addEventListener("focusin", onFocusIn);
    return () => document.removeEventListener("focusin", onFocusIn);
  }, [apply]);

  const isFocused = useCallback(
    (row: number, col: number) => movedRef.current && row === focusRef.current.row && col === focusRef.current.col,
    [],
  );
  const focusedCol = useCallback((row: number) => (focusRef.current.row === row ? focusRef.current.col : -1), []);

  return { isFocused, focusedCol, focusAt };
}
